// Param editors for risk-limit rule types.
//
// Each function renders the rule-specific params for one rule type.
// Called from StepRulesEditor as children of RuleToggleEditor.

import { Show } from 'solid-js';

import Dropdown from '../Dropdown';
import NumberInput from '../NumberInput';
import { useMoneyMark } from '../builderWordsContext';
import type { RuleBuilderState } from '../../core/ruleRegistry';

interface ParamProps {
  rule: RuleBuilderState;
  onUpdateParams: (params: Record<string, unknown>) => void;
}

function paramStr(rule: RuleBuilderState, key: string, fallback = ''): string {
  return (rule.params[key] as string) ?? fallback;
}

/** The arm every required picker opens on: one the author has to leave. */
const CHOOSE = { value: '', label: 'Choose\u2026' } as const;

/**
 * A required boolean.
 *
 * A checkbox cannot say "unset" — it is always in one of its two states, so an
 * untouched one states a term the author never chose. These are mandatory
 * terms of the typed declaration, so they are picked, not toggled, and the
 * save refuses until one is.
 */
function RequiredBool(props: {
  label: string;
  rule: RuleBuilderState;
  term: string;
  onUpdateParams: (params: Record<string, unknown>) => void;
}) {
  const chosen = () => {
    const value = props.rule.params[props.term];
    return typeof value === 'boolean' ? (value ? 'yes' : 'no') : '';
  };
  return (
    <div class="ch-field ch-field-sm">
      <span class="ch-label">{props.label}</span>
      <Dropdown
        value={chosen()}
        options={[CHOOSE, { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
        onChange={(v) =>
          props.onUpdateParams({
            ...props.rule.params,
            [props.term]: v === '' ? undefined : v === 'yes',
          })
        }
      />
    </div>
  );
}

/** Which arm of `LockBreachLevel` the author has chosen, or `''` for none. */
function lockBreachChoice(rule: RuleBuilderState): string {
  const level = rule.params['lock_breach_level'];
  if (typeof level === 'string') return level;
  if (level && typeof level === 'object' && 'amount' in level) return 'amount';
  return '';
}

/** The amount arm's number, as the input renders it. */
function lockBreachAmount(rule: RuleBuilderState): string {
  const level = rule.params['lock_breach_level'];
  if (level && typeof level === 'object' && 'amount' in level) {
    return String((level as { amount: unknown }).amount ?? '');
  }
  return '';
}

/** Max Daily Loss: limit_basis, day_start_reference, commission_inclusive */
export function DailyLossParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Limit Basis</span>
        <Dropdown
          value={paramStr(props.rule, 'limit_basis')}
          options={[
            CHOOSE,
            { value: 'day_start_reference', label: 'Day Start Reference' },
            { value: 'initial_balance', label: 'Initial Balance' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({
              ...props.rule.params,
              limit_basis: v,
            })
          }
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Day Start Reference</span>
        <Dropdown
          value={paramStr(props.rule, 'day_start_reference')}
          options={[
            CHOOSE,
            { value: 'equity', label: 'Equity' },
            { value: 'balance', label: 'Balance' },
            { value: 'higher_of_both', label: 'Higher of Both' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({
              ...props.rule.params,
              day_start_reference: v,
            })
          }
        />
      </div>
      <RequiredBool
        label="Commission Inclusive"
        rule={props.rule}
        term="commission_inclusive"
        onUpdateParams={props.onUpdateParams}
      />
    </div>
  );
}

/** Max Total Drawdown: commission_inclusive only */
export function TotalDrawdownParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <RequiredBool
        label="Commission Inclusive"
        rule={props.rule}
        term="commission_inclusive"
        onUpdateParams={props.onUpdateParams}
      />
    </div>
  );
}

/** Max Trailing Drawdown: peak_basis, update_mode, commission_inclusive, lock */
export function TrailingDrawdownParams(props: ParamProps) {
  const mark = useMoneyMark();
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Peak Basis</span>
        <Dropdown
          value={paramStr(props.rule, 'peak_basis')}
          options={[
            CHOOSE,
            { value: 'balance', label: 'Balance' },
            { value: 'equity', label: 'Equity' },
            { value: 'higher_of_both', label: 'Higher of Both' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, peak_basis: v })
          }
        />
      </div>
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Update Mode</span>
        <Dropdown
          value={paramStr(props.rule, 'update_mode')}
          options={[
            CHOOSE,
            { value: 'realtime', label: 'Realtime' },
            { value: 'eod', label: 'End of Day' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, update_mode: v })
          }
        />
      </div>
      <div class="ch-field ch-field-sm">
        {/* WHAT THE PERCENTAGE IS OF. A trailing allowance can be a share of
            the peak it trails or of the capital the attempt started with, and
            the two are different products at the same number. */}
        <span class="ch-label">Threshold Basis</span>
        <Dropdown
          value={paramStr(props.rule, 'threshold_basis')}
          options={[
            CHOOSE,
            { value: 'peak', label: 'Peak' },
            { value: 'initial_balance', label: 'Initial Balance' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, threshold_basis: v })
          }
        />
      </div>
      <div class="ch-field ch-field-sm">
        {/* WHERE THE FLOOR STOPS FALLING, if it stops. `never` is a real
            product and so is a fixed amount; neither is a default the editor
            may pick, which is why the picker opens unset. */}
        <span class="ch-label">Lock Breach Level</span>
        <Dropdown
          value={lockBreachChoice(props.rule)}
          options={[
            CHOOSE,
            { value: 'never', label: 'Never locks' },
            { value: 'initial_balance', label: 'At initial balance' },
            { value: 'amount', label: 'At an amount' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({
              ...props.rule.params,
              lock_breach_level: v === 'amount' ? { amount: '' } : v || undefined,
            })
          }
        />
      </div>
      <Show when={lockBreachChoice(props.rule) === 'amount'}>
        <div class="ch-field ch-field-sm">
          <span class="ch-label">Lock At ({mark})</span>
          <NumberInput
            value={lockBreachAmount(props.rule)}
            onInput={(v) =>
              props.onUpdateParams({
                ...props.rule.params,
                lock_breach_level: { amount: v },
              })
            }
            min={0}
            step={1000}
          />
        </div>
      </Show>
      <RequiredBool
        label="Commission Inclusive"
        rule={props.rule}
        term="commission_inclusive"
        onUpdateParams={props.onUpdateParams}
      />
    </div>
  );
}

/** Max Open Loss Per Trade (`max_risk_per_trade`): reference_basis */
export function RiskPerTradeParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        <span class="ch-label">Reference Basis</span>
        <Dropdown
          value={paramStr(props.rule, 'reference_basis')}
          options={[
            CHOOSE,
            { value: 'balance_at_open', label: 'Balance at Open' },
            { value: 'equity_at_open', label: 'Equity at Open' },
            { value: 'higher_of_both_at_open', label: 'Higher of Both' },
            { value: 'lower_of_both_at_open', label: 'Lower of Both' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, reference_basis: v })
          }
        />
      </div>
    </div>
  );
}

/** Max Total Unrealized Loss: `limit_basis`. */
export function TotalUnrealizedLossParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        {/* WHAT THE OPEN LOSS IS MEASURED AGAINST. Live equity moves with the
            position; the opening capital does not, and the same percentage
            against each is a different product. */}
        <span class="ch-label">Limit Basis</span>
        <Dropdown
          value={paramStr(props.rule, 'limit_basis')}
          options={[
            CHOOSE,
            { value: 'equity', label: 'Equity' },
            { value: 'initial_balance', label: 'Initial Balance' },
          ]}
          onChange={(v) =>
            props.onUpdateParams({ ...props.rule.params, limit_basis: v })
          }
        />
      </div>
    </div>
  );
}

/** Max Total Exposure: `unit`. */
export function TotalExposureParams(props: ParamProps) {
  return (
    <div class="ch-rule-params-row">
      <div class="ch-field ch-field-sm">
        {/* WHAT THE NUMBER COUNTS. Lots count contracts as the broker writes
            them; weighted contracts count them through the pinned instrument
            weights, so one cap can span instruments of different sizes. */}
        <span class="ch-label">Unit</span>
        <Dropdown
          value={paramStr(props.rule, 'unit')}
          options={[
            CHOOSE,
            { value: 'lots', label: 'Lots' },
            { value: 'weighted_contracts', label: 'Weighted Contracts' },
          ]}
          onChange={(v) => props.onUpdateParams({ ...props.rule.params, unit: v })}
        />
      </div>
    </div>
  );
}
